/**
 * OPS-11: Guest Intelligence Panel
 *
 * Unified guest experience view merging Guest Radar (OPS-8) signals
 * with Leading Indicators (predictive early warnings).
 *
 * Sections:
 *   1. Guest Experience Score + status
 *   2. Active Guest Risks
 *   3. Early Warning Indicators
 *
 * Pure frontend derivation — no backend changes required.
 */

import { useMemo } from 'react';
import { InfoTooltip } from './InfoTooltip';
import type {
  GuestRadarStatus,
  ControlTowerState,
} from '../../services/controlTowerTypes';
import type { PulseTrend } from '../../../utils/computeOperationalConfidence';
import { lowestDimension } from '../../../utils/pressureUtils';

// ─── Status Styles ───────────────────────────────────────────────────────────

const STATUS_COLORS: Record<GuestRadarStatus, string> = {
  excellent: 'text-emerald-400',
  healthy:   'text-green-400',
  warning:   'text-amber-400',
  critical:  'text-rose-400',
};

const STATUS_BG: Record<GuestRadarStatus, string> = {
  excellent: 'bg-emerald-500/10',
  healthy:   'bg-green-500/10',
  warning:   'bg-amber-500/10',
  critical:  'bg-rose-500/10',
};

// ─── Leading Indicator Detection ─────────────────────────────────────────────

interface LeadingIndicator {
  id: string;
  message: string;
  icon: string;
}

function detectIndicators(
  state: ControlTowerState,
  trend: PulseTrend,
): LeadingIndicator[] {
  const indicators: LeadingIndicator[] = [];
  const { alerts, guestRadar, pulse } = state;

  const hkAlerts = alerts.filter((a) => (a.type ?? '').toUpperCase().includes('HOUSEKEEPING'));
  if (hkAlerts.length > 0 && pulse.dimensions.service < 70) {
    indicators.push({ id: 'hk-backlog', message: 'Housekeeping demand increasing', icon: '🏨' });
  }

  const criticalAlerts = alerts.filter((a) => a.severity === 'critical');
  if (criticalAlerts.length === 0 && guestRadar.risks.length > 0) {
    indicators.push({ id: 'guest-pressure', message: 'Guest service pressure forming', icon: '👤' });
  }

  const maintAlerts = alerts.filter((a) => (a.type ?? '').toUpperCase().includes('MAINTENANCE'));
  if (maintAlerts.length > 0 && pulse.dimensions.infrastructure < 70) {
    indicators.push({ id: 'maint-load', message: 'Maintenance workload building', icon: '🔧' });
  }

  const weakest = lowestDimension(pulse.dimensions);
  if (weakest.score < 60 && indicators.every((i) => i.id !== 'hk-backlog' && i.id !== 'maint-load')) {
    indicators.push({ id: 'pressure-forming', message: `${weakest.name} pressure increasing`, icon: '⚡' });
  }

  if (trend === 'deteriorating') {
    indicators.push({ id: 'stability-declining', message: 'Operational stability declining', icon: '📉' });
  }

  return indicators.slice(0, 3);
}

// ─── Component ───────────────────────────────────────────────────────────────

interface GuestIntelligencePanelProps {
  state: ControlTowerState;
  pulseTrend: PulseTrend;
}

export function GuestIntelligencePanel({ state, pulseTrend }: GuestIntelligencePanelProps) {
  const radar = state.guestRadar;
  const indicators = useMemo(() => detectIndicators(state, pulseTrend), [state, pulseTrend]);
  const riskCount = radar.risks.length;
  const risksBySeverity = useMemo(
    () => [...radar.risks].sort((a, b) => {
      const order = { critical: 0, warning: 1, info: 2 };
      return (order[a.severity] ?? 3) - (order[b.severity] ?? 3);
    }),
    [radar.risks],
  );

  return (
    <div className="min-h-[160px] rounded-2xl bg-slate-800/50 border border-slate-700/50 p-4 shadow-lg">
      {/* ── Guest Experience Score ── */}
      <div className="flex items-center gap-4 mb-3">
        <div
          className={`text-3xl font-bold ${STATUS_COLORS[radar.status]}`}
          aria-label={`Guest experience score ${radar.score} out of 100`}
        >
          {radar.score}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-slate-200 flex items-center gap-1.5">Guest Experience <InfoTooltip text="The Guest Experience score estimates overall guest satisfaction by analyzing active risks, complaints, service delays, and sentiment trends across all current guests." /></h2>
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${STATUS_COLORS[radar.status]} ${STATUS_BG[radar.status]}`}>
              {radar.status}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {riskCount > 0 ? `${riskCount} risk${riskCount !== 1 ? 's' : ''} detected` : 'No guests at risk'}
          </p>
        </div>
      </div>

      {/* ── Active Risks ── */}
      <div className="mb-3">
        <h3 className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Active Risks</h3>
        {risksBySeverity.length > 0 ? (
          <div className="space-y-1">
            {risksBySeverity.map((risk, i) => (
              <div key={`${risk.type}-${i}`} className="flex items-center gap-2 h-8 px-2.5 rounded-lg bg-slate-900/40">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  risk.severity === 'critical' ? 'bg-rose-400' :
                  risk.severity === 'warning' ? 'bg-amber-400' : 'bg-sky-400'
                }`} />
                <span className="text-xs text-slate-300 truncate flex-1">{risk.explanation}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  risk.severity === 'critical' ? 'bg-rose-500/20 text-rose-400' :
                  risk.severity === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-sky-500/20 text-sky-400'
                }`}>
                  {risk.severity}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-emerald-400">
            <span>✔</span>
            <span>No guests currently at risk</span>
          </div>
        )}
      </div>

      {/* ── Leading Indicators ── */}
      <div>
        <h3 className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Early Warnings</h3>
        {indicators.length > 0 ? (
          <div className="space-y-1">
            {indicators.map((ind) => (
              <div
                key={ind.id}
                className="flex items-center gap-2 h-8 px-2.5 rounded-lg bg-amber-500/5 border-l-2 border-amber-500/30"
              >
                <span className="text-xs flex-shrink-0">{ind.icon}</span>
                <span className="text-xs text-amber-300/90 font-medium truncate">{ind.message}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No emerging guest experience risks detected.</p>
        )}
      </div>

      {/* ── All Clear ── */}
      {risksBySeverity.length === 0 && indicators.length === 0 && (
        <p className="text-xs text-slate-500 text-center py-2">No active risks — guests are happy</p>
      )}
    </div>
  );
}
